import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/Dialog';
import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { Button } from '@/routes/_embaUserLayout/-components/Button';
import { safeHtml } from '@/utils/safeHtml';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import classNames from 'classnames';
import type React from 'react';
import failedIcon from '../../-assets/images/failed.svg';
import successIcon from '../../-assets/images/success.svg';
import { Explanation } from '../Explanation';
import styles from './index.module.scss';

export const COURSE_LESSON_PRACTICE_SELECT_QUESTION = graphql(/* GraphQL */ `
  fragment EmbaCourseLessonPracticeSelectQuestion on PracticeSelectQuestion {
    id
    explanationContent
    explanationAttachmentImageUrl
    exampleContent
    exampleAttachmentImageUrl
    choices {
      id
      content
    }
    userAnswer {
      id
      isCorrect
      answerChoice{
        id
        embaPracticeSelectQuestionChoiceId
      }
      correctChoice {
        id
        content
      }
    }
  }
`);

export type SelectQuestionProps = {
  question: FragmentType<typeof COURSE_LESSON_PRACTICE_SELECT_QUESTION>;
  isAlreadyAnswered: boolean;
  answer: string;
  setAnswer: (value: string) => void;
  handleAnswerPracticeSelectQuestion: () => void;
  handleNext: () => void;
  isLastQuestion: boolean;
  allAnswered: boolean;
  isAnsweredModalOpen: boolean;
  closeAnsweredModal: () => void;
  scrollToExplanation: () => void;
  answerPracticeSelectQuestionLoading: boolean;
};

export const SelectQuestion: React.FC<SelectQuestionProps> = ({
  question,
  isAlreadyAnswered,
  answer,
  setAnswer,
  handleAnswerPracticeSelectQuestion,
  handleNext,
  isLastQuestion,
  allAnswered,
  isAnsweredModalOpen,
  closeAnsweredModal,
  scrollToExplanation,
  answerPracticeSelectQuestionLoading,
}) => {
  const questionData = useFragment(
    COURSE_LESSON_PRACTICE_SELECT_QUESTION,
    question,
  );

  return (
    <>
      <ul className={styles.questionChoice}>
        {questionData.choices.map((choice) => {
          const isSelected =
            questionData.userAnswer?.answerChoice
              .embaPracticeSelectQuestionChoiceId === choice.id;

          return (
            <li key={choice.id}>
              <input
                type="radio"
                id={choice.id}
                name="answer"
                value={choice.id}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={isAlreadyAnswered}
                checked={isAlreadyAnswered ? isSelected : answer === choice.id}
              />
              <label
                htmlFor={choice.id}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: サニタイズ済みのHTMLを表示するため
                dangerouslySetInnerHTML={{
                  __html: safeHtml(choice.content),
                }}
              />
            </li>
          );
        })}
      </ul>
      {!questionData.userAnswer && (
        <div className={styles.answerButton}>
          <Button
            onClick={() => {
              handleAnswerPracticeSelectQuestion();
            }}
            disabled={!answer}
            // Loadingが終わってからanswer取得するまでに時間がかかるため、isAnsweredModalOpenも利用
            loading={answerPracticeSelectQuestionLoading || isAnsweredModalOpen}
          >
            判定
          </Button>
        </div>
      )}
      {questionData.userAnswer && (
        <div className={styles.answerArea}>
          {!questionData.userAnswer.isCorrect && (
            <p className={styles.answerDetail}>
              <span className={styles.answerResult}>不正解</span>
              <img className={styles.resultIcon} src={failedIcon} alt="" />
              <span
                className={styles.answerContent}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: サニタイズ済みのHTMLを表示するため
                dangerouslySetInnerHTML={{
                  __html: safeHtml(
                    questionData.choices.find(
                      (choice) =>
                        choice.id ===
                        questionData.userAnswer?.answerChoice.embaPracticeSelectQuestionChoiceId.toString(),
                    )?.content,
                  ),
                }}
              />
            </p>
          )}
          <p className={styles.answerDetail}>
            <span className={styles.answerResult}>正解</span>
            <img className={styles.resultIcon} src={successIcon} alt="" />
            <span
              className={styles.answerContent}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: サニタイズ済みのHTMLを表示するため
              dangerouslySetInnerHTML={{
                __html: safeHtml(questionData.userAnswer.correctChoice.content),
              }}
            />
          </p>

          <Explanation
            explanationContent={questionData.explanationContent}
            explanationAttachmentImageUrl={
              questionData.explanationAttachmentImageUrl
            }
          />

          <div className={styles.answerButton}>
            <Button
              onClick={() => {
                handleNext();
              }}
            >
              {isLastQuestion && allAnswered ? '終了' : '次の問題へ'}
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={isAnsweredModalOpen && isAlreadyAnswered}
        onOpenChange={closeAnsweredModal}
      >
        <VisuallyHidden>
          <DialogDescription>演習問題解答結果のモーダル</DialogDescription>
        </VisuallyHidden>

        <DialogContent>
          <div className={styles.AnsweredDialog}>
            {questionData.userAnswer && (
              <>
                <DialogTitle className={styles.AnsweredDialogTitle}>
                  {questionData.userAnswer.isCorrect ? (
                    <>正解です</>
                  ) : (
                    <>不正解です</>
                  )}
                </DialogTitle>
                <div
                  className={classNames(styles.AnsweredDialogDetailArea, {
                    [styles.failed]: !questionData.userAnswer.isCorrect,
                  })}
                >
                  {!questionData.userAnswer.isCorrect && (
                    <p className={styles.AnsweredDialogDetail}>
                      <span className={styles.AnsweredDialogDetailResultIcon}>
                        <img
                          className={styles.resultIcon}
                          src={failedIcon}
                          alt=""
                        />
                      </span>
                      <span
                        className={styles.AnsweredDialogDetailText}
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: サニタイズ済みのHTMLを表示するため
                        dangerouslySetInnerHTML={{
                          __html: safeHtml(
                            questionData.choices.find(
                              (choice) =>
                                choice.id ===
                                questionData.userAnswer?.answerChoice.embaPracticeSelectQuestionChoiceId.toString(),
                            )?.content,
                          ),
                        }}
                      />
                    </p>
                  )}
                  <p className={styles.AnsweredDialogDetail}>
                    <span className={styles.AnsweredDialogDetailResultIcon}>
                      <img
                        className={styles.resultIcon}
                        src={successIcon}
                        alt=""
                      />
                    </span>
                    <span
                      className={styles.AnsweredDialogDetailText}
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: サニタイズ済みのHTMLを表示するため
                      dangerouslySetInnerHTML={{
                        __html: safeHtml(
                          questionData.userAnswer.correctChoice.content,
                        ),
                      }}
                    />
                  </p>
                </div>

                <Button
                  onClick={() => {
                    scrollToExplanation();
                  }}
                >
                  解説を見る
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
